import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import SvgSpinner from '@/components/icons/spinner'
import SvgSpinnerBlue from '@/components/icons/spinner-blue'
import {
  deleteBookedCourseSelectedTheme,
  getBookedCourseSelectedThemes,
  saveBookedCourseSelectedTheme,
} from '@/firebase/booked-courses'
import {
  firebaseGetSchemaSubcollection,
  firebaseGetSingleDocFromDb,
} from '@/firebase/utils'
import {
  IBookedCourse,
  IBookedCourseRaw,
  IBookedCourseSelectedTheme,
  ISchemaThemeDto,
  IUser,
} from '@/types/firebase'
import { IGenericError } from '@/types/shared'
import { notify } from '@/utils/index'
import styles from '../styles/index.module.css'

interface ITheme {
  id: string
  isSelected: boolean
  title: string
}

interface Props {
  student: IUser
  courseId: string
  courseTitle: string
  bookedCourseId: string
  refetchCourseDetails: (useIsLoading?: boolean) => Promise<void>
  setIsLoadingTopNodesSelection: React.Dispatch<React.SetStateAction<boolean>>
}

export const SelectCourseThemesModalContent: FC<Props> = ({
  student,
  courseId,
  courseTitle,
  bookedCourseId,
  refetchCourseDetails,
  setIsLoadingTopNodesSelection,
}) => {
  const [themes, setThemes] = useState<ITheme[]>()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingThemeId, setIsLoadingThemeId] = useState<string>()

  const getThemes = useCallback(async () => {
    if (!(courseId && bookedCourseId)) {
      return
    }

    if (!student || !student.uid) {
      return
    }

    try {
      setIsLoading(true)

      const bookedCourse: IBookedCourse | undefined =
        await firebaseGetSingleDocFromDb<IBookedCourseRaw, IBookedCourse>(
          'bookedCourses',
          bookedCourseId,
        )

      if (!bookedCourse) {
        throw new Error('Unexpected error. Booked course not found')
      }

      const schema: ISchemaThemeDto[] = await firebaseGetSchemaSubcollection(
        courseId,
      )

      const selectedThemes: IBookedCourseSelectedTheme[] =
        await getBookedCourseSelectedThemes(bookedCourseId)

      const themes: ITheme[] = schema.map((theme) => {
        const isSelected = !!selectedThemes.find((s) => s.id === theme.id)

        return { id: theme.id, title: theme.title, isSelected }
      })

      setThemes(themes)
    } catch (error: unknown) {
      console.error('error  :>> ', (error as IGenericError)?.message || error)

      if (error && typeof error === 'object' && 'message' in error) {
        notify('error', error.message)
      }
    } finally {
      setIsLoading(false)
    }
  }, [bookedCourseId, courseId, student])

  // get themes
  useEffect(() => {
    getThemes()
  }, [getThemes])

  const themeOnClickHandler = useCallback(
    async (targetTheme: ITheme, themes: ITheme[]) => {
      if (!(courseId && bookedCourseId)) {
        return
      }

      if (!student || !student.uid) {
        return
      }

      try {
        setIsLoadingThemeId(targetTheme.id)
        setIsLoadingTopNodesSelection(true)

        let notificationText = ''

        if (targetTheme.isSelected) {
          const selectedThemesCount = themes.filter((t) => t.isSelected).length

          if (selectedThemesCount === 1) {
            notify('error', 'At least one topic should be selected!')

            return
          }

          await deleteBookedCourseSelectedTheme(bookedCourseId, targetTheme.id)

          notificationText = `Topic "${targetTheme.title}" deselected`
        } else {
          await saveBookedCourseSelectedTheme(bookedCourseId, {
            themeId: targetTheme.id,
          })

          notificationText = `Topic "${targetTheme.title}" selected`
        }

        const updatedThemes = [...themes].map((theme) => {
          if (theme.id === targetTheme.id) {
            return { ...theme, isSelected: !theme.isSelected }
          }

          return theme
        })

        !!notificationText && notify('success', notificationText)

        setThemes(updatedThemes)

        await refetchCourseDetails(false)
      } catch (error: unknown) {
        console.error('error  :>> ', (error as IGenericError)?.message || error)

        if (error && typeof error === 'object' && 'message' in error) {
          notify('error', error.message)
        }
      } finally {
        setIsLoadingThemeId(undefined)
      }
    },
    [
      bookedCourseId,
      courseId,
      refetchCourseDetails,
      setIsLoadingTopNodesSelection,
      student,
    ],
  )

  const selectedThemesCount: string | undefined = useMemo(() => {
    if (!themes) {
      return undefined
    }
    const selectedThemes = themes.filter((t) => t.isSelected)
    return `${selectedThemes.length}/${themes.length}`
  }, [themes])

  return (
    <div className="min-w-[calc(100vw-80px)] lg:min-w-[860px] max-w-[860px] h-full overflow-hidden flex flex-col gap-6">
      <div className="pt-8 flex flex-col gap-2">
        <div className="py-2 px-1 flex items-center justify-between gap-2">
          <h2 className="text-xl text-v8-ink font-semibold">
            {`${courseTitle} Topics:`}
          </h2>
          {selectedThemesCount && (
            <p className="text-xl text-v8-ink font-semibold">
              {selectedThemesCount}
            </p>
          )}
        </div>
        {/* Divider */}
        <div
          id="divider"
          className="h-[1px] w-full bg-v8-line self-center"
        ></div>
      </div>

      {isLoading ? (
        <div>
          <SvgSpinnerBlue withyMargin={false} />
        </div>
      ) : (
        <>
          {themes && (
            <ul
              className={`pr-1 flex flex-wrap gap-2 md:gap-3 overflow-scroll ${styles.thinScrollbar}`}
            >
              {themes.map((theme) => {
                const { isSelected, id, title } = theme
                const isLoadingTheme = theme.id === isLoadingThemeId

                return (
                  <li key={id}>
                    <button
                      disabled={isLoading || isLoadingTheme}
                      type="button"
                      onClick={() => themeOnClickHandler(theme, themes)}
                      className={`relative py-2 px-4 border border-v8-violet rounded-md ${
                        isSelected ? 'bg-v8-violet' : 'bg-white'
                      }`}
                      style={{
                        cursor: isLoadingTheme ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <p
                        className={`text-xs md:text-base font-semibold text-nowrap ${
                          isSelected ? 'text-white' : 'text-v8-violet'
                        }`}
                      >
                        {title}
                      </p>
                      {isLoadingTheme && (
                        <span
                          className={`absolute w-full h-full top-0 right-0 left-0 flex items-center justify-center border border-transparent rounded-md ${
                            isSelected ? 'bg-v8-violet/80' : 'bg-white/80'
                          }`}
                        >
                          <SvgSpinner
                            className={`inline w-4 h-4 ${
                              isSelected ? 'text-white' : 'text-v8-violet'
                            }`}
                          />
                        </span>
                      )}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </>
      )}
    </div>
  )
}
